import { SyntheticEvent, useState } from 'react';
import { ApolloError, gql, useMutation, useQuery } from '@apollo/client';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { settingsState } from 'components/common/Managers/SettingsManager.state';

import * as K from 'common/constants';
import { HELP_ROUTE } from 'components/help/constants';
import { clearGraphQLErrorMessage } from 'common/util/string';
import ELEMENT from 'common/element';
import { Access, Key } from 'common/graphqlTypes';
import useCopyToClipboard from 'hooks/useCopyToClipboard';

import Page from 'components/common/Page/Page';
import PageHeader from 'components/common/PageHeader/PageHeader';
import PromptDialog from 'components/common/Dialog/PromptDialog';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import ConfirmDialog from 'components/common/Dialog/ConfirmDialog';
import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';
import ErrorPlaceholder from 'components/common/ErrorPlaceholder/ErrorPlaceholder';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import { timezoneMoment } from 'common/util/time';

import ApiKeysSection from './ApiKeysSection';
import './ApiKeysPage.scss';

import { ADD_KEY, DELETE_KEY, GET_KEYS, RENAME_KEY } from './ApiKeysQueries';

type ApiKeysCache = {
  keys: Array<Key>;
};

/**
 * ApiKeysPage allows the user to view, edit, and delete API keys. There are currently
 * two types of API keys: log keys and config keys. A key can have either read or write
 * access.
 */

function ApiKeysPage(): JSX.Element {
  // system settings
  const [settings] = useRecoilState(settingsState);
  const timezone = useRecoilValue(timezoneState);

  // state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [currentKeyName, setCurrentKeyName] = useState('');
  const [currentKeyId, setCurrentKeyId] = useState('');
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const copy = useCopyToClipboard();

  // data hooks

  // get key data
  const showErrorAlert = (message: string): void => {
    setPageAlertConfig({ message: clearGraphQLErrorMessage(message), variant: PageAlertVariant.ERROR, autoHide: true });
  };

  const { loading, error, data } = useQuery(GET_KEYS, {
    onError: (apolloError: ApolloError) => {
      showErrorAlert(apolloError.message);
    },
  });

  // change the label of a key
  const [renameKey] = useMutation(RENAME_KEY, {
    onError: (apolloError: ApolloError) => {
      showErrorAlert(apolloError.message);
    },
  });

  // TODO: just refetch after a mutation

  // add a new key
  const [addKey] = useMutation(ADD_KEY, {
    update: (cache, { data: { createKey: addedKey } }) => {
      const query = gql`
        {
          keys {
            id
          }
        }
      `;
      const result: ApiKeysCache | null = cache.readQuery({ query });
      if (result !== null) {
        const { keys: cachedKeys }: ApiKeysCache = result;
        const newKeys = [...cachedKeys];
        newKeys.push(addedKey);
        cache.writeQuery({ query, data: { keys: newKeys } });
      }
    },
    onError: (apolloError: ApolloError) => {
      showErrorAlert(apolloError.message);
    },
    onCompleted: () => {
      if (settings[K.SETTING.ENABLE_HIDDEN_API_KEYS]) {
        setPageAlertConfig({
          message: 'This is the last time the key will be shown in the clear, so please make a copy of it now',
          variant: PageAlertVariant.INFO,
          autoHide: false,
        });
      }
    },
  });

  // remove a key
  const [deleteKey] = useMutation(DELETE_KEY, {
    update: (cache, { data: { deleteKey: deletedKey } }) => {
      const query = gql`
        {
          keys {
            id
          }
        }
      `;
      const result: ApiKeysCache | null = cache.readQuery({ query });
      if (result !== null) {
        const { keys: cachedKeys }: ApiKeysCache = result;
        let newKeys = [...cachedKeys];
        newKeys = newKeys.filter((key) => key.id !== deletedKey.id);
        cache.writeQuery({ query, data: { keys: newKeys } });
      }
    },
    onError: (apolloError: ApolloError) => {
      showErrorAlert(apolloError.message);
    },
  });

  if (loading || !data) {
    return <LoadingPlaceholder />;
  }

  if (error) {
    return <ErrorPlaceholder error={error} />;
  }

  // divvy keys by type
  const logKeys = data.keys.filter((key: Key) => key.type === K.LOG_KEY_TYPE);
  const configKeys = data.keys.filter((key: Key) => key.type === K.CONFIG_KEY_TYPE);

  // user action handlers

  // send request to add key, then add it to local data
  // new key gets today's date as default label
  const handleAddKey = (type: string, access: Access): void => {
    const label = timezoneMoment(timezone)().format('MMM D, YYYY');
    addKey({ variables: { access, label, type } });
  };

  const handleChange = (ev: SyntheticEvent): void => {
    const eventTarget = ev.target as HTMLInputElement;
    setCurrentKeyName(eventTarget.value);
  };

  // user wants to rename a key
  const openEditDialog = (): void => setShowEditDialog(true);

  // user wants to delete a key
  const openDeleteDialog = (): void => setShowDeleteDialog(true);

  const handleCopyKey = (key: Key): void => copy(key.value, 'key');

  // user wants to rename a key
  const handleEditKey = (key: Key): void => {
    setCurrentKeyName(key.label);
    setCurrentKeyId(key.id);
    openEditDialog();
  };

  // user wants to delete a key
  const handleDeleteKey = (key: Key): void => {
    setCurrentKeyName(key.label);
    setCurrentKeyId(key.id);
    openDeleteDialog();
  };

  // user is removing a key
  const doDeleteKey = (): void => {
    deleteKey({ variables: { id: currentKeyId } });
  };

  // user has finished with the Rename Key dialog
  const doRenameKey = (): void => {
    renameKey({ variables: { id: currentKeyId, label: currentKeyName } });
    setCurrentKeyName('');
  };

  const deleteText = 'Delete API key "' + currentKeyName + '" ?';

  // The page has a header followed by two sections with keys laid out in a tabular format.
  // A modal dialog is used for renaming a key.
  return (
    <Page docTitle="API Keys" testId={ELEMENT.apiKeysPage}>
      <PageHeader title="Manage API Keys" helpLink={HELP_ROUTE.HELP_API_KEYS} />
      <div>You can send and retrieve data via the Scalyr Agent or our Java API.</div>
      <ApiKeysSection
        apiKeysList={logKeys}
        sectionName={K.LOG_ACCESS_TITLE}
        handleAddKey={handleAddKey}
        handleCopyKey={handleCopyKey}
        handleEditKey={handleEditKey}
        handleDeleteKey={handleDeleteKey}
        testId={ELEMENT.apiKeysLogAccess}
      />
      <ApiKeysSection
        apiKeysList={configKeys}
        sectionName={K.CONFIG_ACCESS_TITLE}
        handleAddKey={handleAddKey}
        handleCopyKey={handleCopyKey}
        handleEditKey={handleEditKey}
        handleDeleteKey={handleDeleteKey}
        testId={ELEMENT.apiKeysConfigAccess}
      />
      <PromptDialog
        title="Rename API Key"
        label="Key name"
        show={showEditDialog}
        close={(): void => setShowEditDialog(false)}
        value={currentKeyName}
        onChange={handleChange}
        onSubmit={doRenameKey}
        testId={ELEMENT.apiKeysEditDialog}
      />
      <ConfirmDialog
        title="Delete API Key"
        text={deleteText}
        show={showDeleteDialog}
        close={(): void => setShowDeleteDialog(false)}
        onSubmit={doDeleteKey}
      />
    </Page>
  );
}

export default ApiKeysPage;
